/**
 * User Management Page
 *
 * Authenticated page for listing, editing, and deleting users.
 * No "create new" action — new users can only be created via the
 * self-service signup flow.
 */

import { useState, useCallback, useId } from 'react';
import {
  getRoleDisplayName,
  type User,
  type UserRole,
} from '@course-authoring/shared-types';
import {
  AdminPage,
  AdminPageHeader,
  AdminPanel,
  adminButton,
  adminField,
  adminStatusStyle,
  adminTokens,
  createAdminListStyles,
} from '../admin-ui';
import { useAuth } from '../auth';
import {
  useUsersQuery,
  useUpdateUserMutation,
  useDeleteUserMutation,
} from '../api/userApi';

const ALL_ROLES: UserRole[] = [
  'viewer',
  'author',
  'reviewer',
  'company_admin',
  'super_admin',
];

export function UserManagementPage() {
  const { user: currentUser } = useAuth();
  const { data: users, isLoading, error } = useUsersQuery();

  // Delete state
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  // Edit state
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [editName, setEditName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editRole, setEditRole] = useState<UserRole>('viewer');
  const [editError, setEditError] = useState<string | null>(null);

  // Accessibility ids
  const editDialogTitleId = useId();
  const deleteDialogTitleId = useId();

  // Mutations
  const { mutate: updateUser, isPending: isUpdating } = useUpdateUserMutation({
    onSuccess: () => {
      setEditingUser(null);
      setEditError(null);
    },
    onError: (err) => {
      setEditError(err.message || 'Failed to update user');
    },
  });

  const { mutate: deleteUser, isPending: isDeleting } = useDeleteUserMutation({
    onSuccess: () => {
      setDeleteConfirmId(null);
      setDeleteConfirmText('');
    },
  });

  // Handlers
  const handleEditClick = useCallback((user: User) => {
    setEditingUser(user);
    setEditName(user.name);
    setEditEmail(user.email);
    setEditRole(user.role);
    setEditError(null);
  }, []);

  const handleEditSave = useCallback(() => {
    if (!editingUser || isUpdating) return;
    updateUser({
      userId: editingUser.id,
      payload: {
        name: editName.trim(),
        email: editEmail.trim(),
        role: editRole,
      },
    });
  }, [editingUser, editName, editEmail, editRole, isUpdating, updateUser]);

  const handleDeleteClick = useCallback((userId: string) => {
    setDeleteConfirmText('');
    setDeleteConfirmId(userId);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (deleteConfirmId && deleteConfirmText.toLowerCase() === 'delete') {
      deleteUser(deleteConfirmId);
    }
  }, [deleteConfirmId, deleteConfirmText, deleteUser]);

  const userToDelete = deleteConfirmId
    ? users?.find((u) => u.id === deleteConfirmId)
    : null;

  const pageHeader = (
    <AdminPageHeader
      title="User Management"
      description="Names, emails and roles for everyone who can sign in to AuthorAI."
    />
  );

  if (isLoading) {
    return (
      <AdminPage maxWidth={USERS_MAX_WIDTH}>
        {pageHeader}
        <AdminPanel><p style={styles.loadingText}>Loading users...</p></AdminPanel>
      </AdminPage>
    );
  }

  if (error) {
    return (
      <AdminPage maxWidth={USERS_MAX_WIDTH}>
        {pageHeader}
        <AdminPanel>
          <p style={styles.errorText}>
            Error loading users: {(error as Error).message}
          </p>
        </AdminPanel>
      </AdminPage>
    );
  }

  return (
    <>
      <AdminPage maxWidth={USERS_MAX_WIDTH}>
        {pageHeader}

        {!users || users.length === 0 ? (
          <AdminPanel><p style={styles.emptyText}>No users found.</p></AdminPanel>
        ) : (
          <AdminPanel padded={false}>
            <div style={styles.tableScroll}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Name</th>
                  <th style={styles.th}>Email</th>
                  <th style={styles.th}>Role</th>
                  <th style={{ ...styles.th, textAlign: 'right' as const }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => {
                  const isSelf = user.id === currentUser?.id;
                  return (
                    <tr key={user.id} style={styles.row}>
                      <td style={styles.td}>
                        {user.name}
                        {isSelf && <span style={styles.youBadge}>(you)</span>}
                      </td>
                      <td style={styles.td}>{user.email}</td>
                      <td style={styles.td}>{getRoleDisplayName(user.role)}</td>
                      <td style={{ ...styles.td, textAlign: 'right' as const }}>
                        <button
                          onClick={() => handleEditClick(user)}
                          style={styles.actionButton}
                        >
                          Edit
                        </button>
                        {!isSelf && (
                          <button
                            onClick={() => handleDeleteClick(user.id)}
                            style={styles.actionButtonDanger}
                          >
                            Delete
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          </AdminPanel>
        )}
      </AdminPage>

      {/* Edit User Modal */}
      {editingUser && (
        <div style={styles.modalOverlay}>
          <div
            style={styles.modal}
            role="dialog"
            aria-modal="true"
            aria-labelledby={editDialogTitleId}
          >
            <h3 style={styles.modalTitle} id={editDialogTitleId}>
              Edit User
            </h3>

            {editError && (
              <div style={styles.errorBox} role="alert">
                {editError}
              </div>
            )}

            <div style={styles.field}>
              <label htmlFor="edit-user-name" style={styles.label}>
                Name
              </label>
              <input
                id="edit-user-name"
                type="text"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                style={styles.input}
                disabled={isUpdating}
              />
            </div>

            <div style={styles.field}>
              <label htmlFor="edit-user-email" style={styles.label}>
                Email
              </label>
              <input
                id="edit-user-email"
                type="email"
                value={editEmail}
                onChange={(e) => setEditEmail(e.target.value)}
                style={styles.input}
                disabled={isUpdating}
              />
            </div>

            <div style={styles.field}>
              <label htmlFor="edit-user-role" style={styles.label}>
                Role
              </label>
              <select
                id="edit-user-role"
                value={editRole}
                onChange={(e) => setEditRole(e.target.value as UserRole)}
                style={styles.select}
                disabled={isUpdating}
              >
                {ALL_ROLES.map((role) => (
                  <option key={role} value={role}>
                    {getRoleDisplayName(role)}
                  </option>
                ))}
              </select>
            </div>

            <div style={styles.modalActions}>
              <button
                onClick={() => setEditingUser(null)}
                style={styles.cancelButton}
                disabled={isUpdating}
              >
                Cancel
              </button>
              <button
                onClick={handleEditSave}
                style={{
                  ...styles.saveButton,
                  ...(isUpdating ? styles.saveButtonDisabled : {}),
                }}
                disabled={isUpdating}
              >
                {isUpdating ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmId && (
        <div style={styles.modalOverlay}>
          <div
            style={styles.modal}
            role="dialog"
            aria-modal="true"
            aria-labelledby={deleteDialogTitleId}
          >
            <h3 style={styles.modalTitle} id={deleteDialogTitleId}>
              Delete User?
            </h3>
            <p style={styles.modalText}>
              Are you sure you want to delete{' '}
              <strong>{userToDelete?.name || 'this user'}</strong>? This action
              cannot be undone.
            </p>
            <p style={styles.modalText}>
              Type <strong>delete</strong> to confirm:
            </p>
            <input
              type="text"
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder="Type 'delete' to confirm"
              style={styles.input}
              autoFocus
            />
            <div style={{ ...styles.modalActions, marginTop: '16px' }}>
              <button
                onClick={() => {
                  setDeleteConfirmId(null);
                  setDeleteConfirmText('');
                }}
                style={styles.cancelButton}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                style={{
                  ...styles.deleteButton,
                  ...(deleteConfirmText.toLowerCase() !== 'delete'
                    ? styles.deleteButtonDisabled
                    : {}),
                }}
                disabled={
                  isDeleting || deleteConfirmText.toLowerCase() !== 'delete'
                }
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

const USERS_MAX_WIDTH = '960px';

const listStyles = createAdminListStyles();

const styles: Record<string, React.CSSProperties> = {
  loadingText: listStyles.stateText,
  errorText: { ...listStyles.stateText, color: adminTokens.status.error.text },
  emptyText: listStyles.stateText,
  tableScroll: listStyles.tableScroll,
  table: listStyles.table,
  th: listStyles.headerCell,
  row: listStyles.tableRow,
  td: listStyles.bodyCell,
  youBadge: {
    marginLeft: '6px',
    fontSize: adminTokens.typography.caption.size,
    color: adminTokens.text.secondary,
    fontStyle: 'italic' as const,
  },
  actionButton: {
    ...adminButton.secondary,
    minHeight: '30px',
    padding: '0 10px',
    fontSize: adminTokens.typography.label.size,
    marginRight: '6px',
  },
  actionButtonDanger: {
    ...adminButton.secondary,
    minHeight: '30px',
    padding: '0 10px',
    fontSize: adminTokens.typography.label.size,
    borderColor: adminTokens.status.error.text,
    color: adminTokens.status.error.text,
  },
  modalOverlay: listStyles.modalOverlay,
  modal: listStyles.modal,
  modalTitle: { ...listStyles.modalTitle, margin: '0 0 16px 0' },
  modalText: { ...listStyles.modalText, margin: '0 0 12px 0' },
  modalActions: { ...listStyles.modalActions, marginTop: '20px' },
  field: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '4px',
    marginBottom: '14px',
  },
  label: { ...adminField.label, marginBottom: 0, fontSize: adminTokens.typography.body.size },
  input: { ...adminField.input, width: '100%', height: '40px' },
  select: { ...adminField.select, width: '100%', height: '40px' },
  errorBox: { ...adminStatusStyle('error'), marginBottom: '14px' },
  cancelButton: adminButton.secondary,
  saveButton: adminButton.primary,
  saveButtonDisabled: adminButton.disabled,
  deleteButton: adminButton.danger,
  deleteButtonDisabled: adminButton.disabled,
};
